import { FiCheck, FiX, FiCpu, FiEdit2 } from "react-icons/fi";
import type {
	AITriageSuggestion,
	TriageStatus,
} from "../../../../types/clarification-review.type";
import { TRIAGE_STATUS_LABELS } from "../../../../types/clarification-review.type";

const SUGGESTION_STATUS_STYLES: Record<string, string> = {
	accepted: "bg-green-100 text-green-700",
	declined: "bg-red-100 text-red-700",
	duplicate: "bg-gray-100 text-gray-700",
};

interface AISuggestionRowProps {
	currentStatus: TriageStatus | null;
	isAccepted: boolean;
	isDismissed: boolean;
	referenceNumber: number;
	suggestion: AITriageSuggestion;
	onAccept: (suggestion: AITriageSuggestion) => void;
	onDismiss: (clarificationId: number) => void;
}

export const AISuggestionRow: React.FC<AISuggestionRowProps> = ({
	suggestion,
	currentStatus,
	referenceNumber,
	onAccept,
	onDismiss,
	isDismissed,
	isAccepted,
}) => {
	const confidencePercent = Math.round(suggestion.confidence * 100);
	const isResolved = isDismissed || isAccepted;
	const matchesLead = currentStatus === suggestion.suggested_status;

	return (
		<div
			className={`rounded-lg border p-3 ${
				isResolved
					? "border-gray-200 bg-gray-50 opacity-60"
					: matchesLead
						? "border-green-200 bg-green-50"
						: "border-indigo-200 bg-white"
			}`}
			data-testid={`ai-suggestion-${suggestion.clarification_id}`}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					{/* Header */}
					<div className="mb-1 flex items-center gap-2">
						<FiCpu className="shrink-0 text-indigo-500" size={12} />
						<span className="text-xs font-medium text-gray-500">#{referenceNumber}</span>
						<span
							className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
								SUGGESTION_STATUS_STYLES[suggestion.suggested_status] ?? "bg-gray-100 text-gray-700"
							}`}
						>
							{TRIAGE_STATUS_LABELS[suggestion.suggested_status]}
						</span>
						<span className="text-xs text-gray-400">{confidencePercent}% confidence</span>
						{matchesLead && !isResolved && (
							<span className="text-xs font-medium text-green-600">Matches your decision</span>
						)}
					</div>

					{/* Reasoning */}
					<p className="text-sm text-gray-600">{suggestion.reasoning}</p>

					{/* Text corrections */}
					{suggestion.text_corrections && (
						<div className="mt-1.5 flex items-start gap-1.5 rounded bg-amber-50 px-2 py-1">
							<FiEdit2 className="mt-0.5 shrink-0 text-amber-600" size={12} />
							<p className="text-xs text-amber-800">
								<span className="font-medium">Suggested correction:</span> {suggestion.text_corrections}
							</p>
						</div>
					)}

					{/* Status indicators */}
					{isAccepted && (
						<span className="mt-1 inline-block text-xs font-medium text-green-600">Accepted</span>
					)}
					{isDismissed && (
						<span className="mt-1 inline-block text-xs font-medium text-gray-400">Dismissed</span>
					)}
				</div>

				{/* Action buttons */}
				{!isResolved && (
					<div className="flex shrink-0 gap-1">
						<button
							type="button"
							className="inline-flex items-center gap-1 rounded-md border border-green-300 bg-white px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50"
							onClick={() => onAccept(suggestion)}
							data-testid={`ai-accept-${suggestion.clarification_id}`}
						>
							<FiCheck size={12} />
							Accept
						</button>
						<button
							type="button"
							className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-50"
							onClick={() => onDismiss(suggestion.clarification_id)}
							data-testid={`ai-dismiss-${suggestion.clarification_id}`}
						>
							<FiX size={12} />
							Dismiss
						</button>
					</div>
				)}
			</div>
		</div>
	);
};
